import React from 'react';
import { ChatMessage, ModalType, Sender, AttachedFile } from '../types';
import { CosmosLogoIcon } from './icons';
import { SamplePromptCard } from './SamplePromptCard';
import { ChatMessageComponent } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { FollowUpQuestions } from './FollowUpQuestions';
import { ThinkingAnimation } from './ThinkingAnimation';
import { StreamStatus } from '../services/apiService';
import { trackClick } from '../utils/analytics';
import { useSlowLoadWarning } from '../utils/useSlowLoadWarning';

interface ChatInterfaceProps {
    messages: ChatMessage[];
    onSend: (prompt: string, files: File[]) => void;
    isLoading: boolean;
    isExtensiveSearch?: boolean; // Whether current loading is for extensive search
    openModal: (type: ModalType) => void;
    samplePrompts: { title: string; prompt: string; }[];
    welcomeMessage?: string;
    showConciergeLink?: boolean;
    onResetChat?: () => void;
    showResetButton?: boolean;
    // User profile data for contact modal
    userName?: string;
    personalSummary?: string;
    rdSummary?: string;
    // Chat session ID for linking saved items
    sessionId?: string;
    // Profile completion for contact restrictions
    profileIncomplete?: boolean;
    // Active chat ID for auto-focus
    activeChatId?: string | null;
    // Handler to run extensive search
    onRunExtensiveSearch?: () => void;
    // Set of message IDs that were just created (for isFreshMessage logic)
    freshMessageIds?: Set<string>;
    // Streaming status props
    streamingStatus?: StreamStatus | null;
    streamingMessage?: string | null;
    streamingResultsCount?: number;
}

const defaultWelcomeMessage = "Hi! I'm Cosmos — here to help you scout high-potential university innovations. I can help you with";

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
    messages, 
    onSend, 
    isLoading,
    isExtensiveSearch = false,
    openModal, 
    samplePrompts, 
    welcomeMessage = defaultWelcomeMessage, 
    showConciergeLink,
    onResetChat,
    showResetButton = false,
    userName = '',
    personalSummary = '',
    rdSummary = '',
    sessionId = '',
    profileIncomplete = false,
    activeChatId,
    onRunExtensiveSearch,
    freshMessageIds = new Set(),
    streamingStatus = null,
    streamingMessage = null,
    streamingResultsCount = 0
}) => {
  // Surface a "check your internet connection" hint if a chat response takes
  // longer than 5s to come back — almost always the user's network, not us.
  const showSlowNet = useSlowLoadWarning(isLoading, 5000);
  return (
    <main className="flex flex-col flex-1 self-stretch rounded-2xl bg-grayscale-100 shadow-custom-light px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 pt-4">
      <div className="flex-1 flex flex-col justify-between overflow-y-auto hide-scrollbar">
        <div className="flex-grow">
          {messages.length === 0 && !isLoading ? (
            <div className="flex flex-col items-start gap-4">
              <div className="flex items-center gap-2 self-stretch">
                <CosmosLogoIcon />
                <p className="text-grayscale-1200 font-grotesk text-sm">
                  {welcomeMessage}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 self-stretch">
                {samplePrompts.map((p, i) => (
                  <SamplePromptCard key={i} title={p.title} prompt={p.prompt} onClick={(prompt) => { trackClick('cosmos_click_sample_prompt', { prompt: p.title }); onSend(prompt, []); }} />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <ChatMessageComponent 
                  key={msg.id} 
                  message={msg}
                  userName={userName}
                  personalSummary={personalSummary}
                  rdSummary={rdSummary}
                  sessionId={sessionId}
                  profileIncomplete={profileIncomplete}
                  onRunExtensiveSearch={onRunExtensiveSearch}
                  onSendMessage={(text: string) => onSend(text, [])}
                  isFreshMessage={freshMessageIds.has(msg.id)}
                />
              ))}
              {isLoading && (
                 <div className="flex items-start gap-4 p-4 rounded-xl">
                    <div className="flex-shrink-0">
                      <CosmosLogoIcon />
                    </div>
                    <div className="flex-1">
                      {/* Slow-network hint — shows above the thinking animation
                          once loading crosses 5s. */}
                      {showSlowNet && (
                        <div className="mb-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M5 12.55a11 11 0 0 1 14 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M8.5 16.05a6 6 0 0 1 7 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M12 19.5h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                            <path d="M2 8.82a16 16 0 0 1 20 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
                          </svg>
                          This is taking longer than usual — please check your internet connection.
                        </div>
                      )}
                      <ThinkingAnimation
                        isExtensiveSearch={isExtensiveSearch}
                        streamingStatus={streamingStatus}
                        streamingMessage={streamingMessage}
                        resultsCount={streamingResultsCount}
                      />
                    </div>
                  </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-between items-center my-4">
            {/* <p className="text-grayscale-1300 font-grotesk text-xs">Cosmos features cutting-edge technology and research from 10 leading universities across the USA</p> */}
            {showResetButton && onResetChat && messages.length > 0 && (
                <button 
                    onClick={onResetChat}
                    className="text-primary-og-600 font-grotesk text-xs font-bold underline hover:text-primary-700 transition-colors flex-shrink-0 ml-4"
                >
                    Reset Chat
                </button>
            )}
        </div>
      </div>

      {/* Follow-up Questions - TEMPORARILY DISABLED */}
      {/* {messages.length > 0 && !isLoading && (
        <div className="px-4 pb-4">
          <FollowUpQuestions
            chatHistory={messages.map(msg => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.message
            }))}
            onQuestionClick={(question) => onSend(question, [])}
          />
        </div>
      )} */}

      <ChatInput onSend={onSend} isLoading={isLoading} openModal={openModal} showConciergeLink={showConciergeLink} hasMessages={messages.length > 0} activeChatId={activeChatId} />
    </main>
  );
};